/**
 * Hook CRUD pour session_change_requests
 * Supporte les demandes admin→prof ET prof→admin (reports de séance)
 * Inclut realtime, anti-doublon, et compteur pending
 */
import { useState, useCallback, useEffect, useMemo } from 'react'
import { supabase, isDemoMode } from '@/lib/supabase'
import { useAuthContext } from '@/contexts/AuthContext'
import { useEmailNotifications } from '@/hooks/useEmailNotifications'
import { transformSessionChangeRequest } from '@/utils/transforms'
import toast from 'react-hot-toast'
import type { SessionChangeRequest, ChangeRequestType } from '@/types'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { localToISO } from '@/utils/helpers'

// ==================== CONSTANTS ====================

const REASON_LABELS: Record<string, string> = {
  sick: 'Maladie',
  personal: 'Raison personnelle',
  training: 'Formation',
  other: 'Autre',
}

// ==================== DEMO DATA ====================
const DEMO_CHANGE_REQUESTS: SessionChangeRequest[] = [
  {
    id: 'demo-cr-1', sessionId: 'demo-session-1', teacherId: 'demo-teacher-1', centerId: 'demo-center',
    changeType: 'time_change', oldValues: { start_time: '08:00', end_time: '10:00' }, newValues: { start_time: '09:00', end_time: '11:00' },
    status: 'pending', requestedBy: 'demo-admin', message: 'Decalage d\'1h pour raison logistique',
    session: { id: 'demo-session-1', title: 'Mathematiques L1' },
    teacher: { id: 'demo-teacher-1', firstName: 'Marie', lastName: 'Dupont' },
    requester: { id: 'demo-admin', firstName: 'Admin', lastName: 'Centre' },
    createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
  },
  // Demo: teacher-initiated reschedule request
  {
    id: 'demo-cr-2', sessionId: 'demo-session-2', teacherId: 'demo-teacher-1', centerId: 'demo-center',
    changeType: 'time_change',
    oldValues: { start_time: '2026-03-20T14:00:00', end_time: '2026-03-20T16:00:00' },
    newValues: { start_time: '2026-03-22T14:00:00', end_time: '2026-03-22T16:00:00', reason: 'sick', message: 'Rendez-vous medical' },
    status: 'pending', requestedBy: 'demo-teacher-1', message: 'Rendez-vous medical',
    session: { id: 'demo-session-2', title: 'Physique L2' },
    teacher: { id: 'demo-teacher-1', firstName: 'Marie', lastName: 'Dupont' },
    requester: { id: 'demo-teacher-1', firstName: 'Marie', lastName: 'Dupont' },
    createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
  },
]

const CHANGE_TYPE_LABELS: Record<ChangeRequestType, string> = {
  time_change: 'Changement d\'horaire',
  room_change: 'Changement de salle',
  cancel: 'Annulation',
  other: 'Autre',
}

const SELECT_QUERY = `
  *,
  teacher:profiles!session_change_requests_teacher_id_fkey(id, full_name, email),
  requester:profiles!session_change_requests_requested_by_fkey(id, full_name),
  session:training_sessions!session_change_requests_session_id_fkey(id, title, start_time, end_time, room_id, rooms:room_id(name), class_:classes!training_sessions_class_id_fkey(name), subject:subjects!training_sessions_subject_id_fkey(name))
`

interface CreateChangeRequestData {
  sessionId: string
  teacherId: string
  changeType: ChangeRequestType
  oldValues: Record<string, unknown>
  newValues: Record<string, unknown>
  message?: string
}

interface CreateTeacherRescheduleData {
  sessionId: string
  sessionTitle: string
  currentStart: string // ISO
  currentEnd: string // ISO
  proposedDate: string // YYYY-MM-DD
  proposedStartTime: string // HH:mm
  proposedEndTime: string // HH:mm
  reason: string // sick | personal | training | other
  message?: string
}

export { CHANGE_TYPE_LABELS, REASON_LABELS }

function getAppOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : ''
}

export function useSessionChangeRequests() {
  const { user } = useAuthContext()
  const { notifyCollaboration } = useEmailNotifications()
  const [changeRequests, setChangeRequests] = useState<SessionChangeRequest[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // ==================== FETCH ====================

  const fetchChangeRequests = useCallback(async (filter?: { teacherId?: string; status?: string }) => {
    if (isDemoMode) {
      let filtered = DEMO_CHANGE_REQUESTS
      if (filter?.teacherId) filtered = filtered.filter(c => c.teacherId === filter.teacherId)
      if (filter?.status) filtered = filtered.filter(c => c.status === filter.status)
      setChangeRequests(filtered)
      return
    }
    setIsLoading(true)
    try {
      let query = supabase.from('session_change_requests').select(SELECT_QUERY).order('created_at', { ascending: false })
      if (filter?.teacherId) query = query.eq('teacher_id', filter.teacherId)
      if (filter?.status) query = query.eq('status', filter.status)

      const { data, error } = await query
      if (error) throw error
      setChangeRequests((data || []).map(transformSessionChangeRequest))
    } catch (err: any) {
      console.error('Error fetching change requests:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /** Fetch all change requests for the current center (for admin views) */
  const fetchAllForCenter = useCallback(async () => {
    if (isDemoMode) {
      setChangeRequests(DEMO_CHANGE_REQUESTS)
      return
    }
    if (!user) return
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('session_change_requests')
        .select(SELECT_QUERY)
        .eq('center_id', user.establishmentId)
        .order('created_at', { ascending: false })
      if (error) throw error
      setChangeRequests((data || []).map(transformSessionChangeRequest))
    } catch (err: any) {
      console.error('Error fetching change requests:', err)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  const getForTeacher = useCallback(() => {
    if (!user) return
    return fetchChangeRequests({ teacherId: user.id })
  }, [user, fetchChangeRequests])

  const getPending = useCallback(() => {
    return fetchChangeRequests({ status: 'pending' })
  }, [fetchChangeRequests])

  /** Filter teacher-initiated reschedule requests (requestedBy === teacherId) */
  const getTeacherInitiated = useCallback(() => {
    return changeRequests.filter(cr => cr.requestedBy === cr.teacherId)
  }, [changeRequests])

  /** Count of pending teacher-initiated reschedule requests (for sidebar badge) */
  const pendingTeacherRescheduleCount = useMemo(
    () => changeRequests.filter(cr => cr.requestedBy === cr.teacherId && cr.status === 'pending').length,
    [changeRequests]
  )

  // ==================== REALTIME ====================

  useEffect(() => {
    if (isDemoMode || !user) return

    const channel = supabase
      .channel(`session-change-requests-realtime:${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'session_change_requests',
      }, () => {
        // Refetch on any change — use the appropriate fetch based on role
        if (user.role === 'teacher' || user.role === 'trainer') {
          fetchChangeRequests({ teacherId: user.id })
        } else {
          fetchAllForCenter()
        }
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, fetchChangeRequests, fetchAllForCenter])

  // ==================== ADMIN → TEACHER (existing) ====================

  const create = useCallback(async (data: CreateChangeRequestData) => {
    if (isDemoMode || !user) return
    try {
      const { error } = await supabase.from('session_change_requests').insert({
        session_id: data.sessionId,
        teacher_id: data.teacherId,
        center_id: user.establishmentId,
        change_type: data.changeType,
        old_values: data.oldValues,
        new_values: data.newValues,
        requested_by: user.id,
        message: data.message || null,
      })
      if (error) throw error
      toast.success('Demande de modification envoyee')

      // Notify teacher
      supabase.from('in_app_notifications').insert({
        user_id: data.teacherId,
        center_id: user.establishmentId,
        title: 'Modification de seance',
        message: data.message || `Modification demandee (${CHANGE_TYPE_LABELS[data.changeType]})`,
        type: 'change_request_pending',
        link: '/teacher-collab',
        session_id: data.sessionId,
      }).then(() => {})

    } catch (err: any) {
      toast.error(err.message || 'Erreur')
    }
  }, [user])

  const accept = useCallback(async (id: string, response?: string) => {
    if (isDemoMode || !user) return
    try {
      const { data: cr, error: fetchErr } = await supabase.from('session_change_requests')
        .select('session_id, new_values, requested_by, change_type')
        .eq('id', id)
        .single()
      if (fetchErr) throw fetchErr

      const { error } = await supabase.from('session_change_requests')
        .update({ status: 'accepted', teacher_response: response || null, responded_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error

      // Apply new_values to training_sessions
      if (cr && cr.change_type !== 'cancel') {
        const newVals = cr.new_values as Record<string, unknown>
        const updateData: Record<string, unknown> = {}
        if (newVals.start_time) updateData.start_time = newVals.start_time
        if (newVals.end_time) updateData.end_time = newVals.end_time
        if (newVals.room_id) updateData.room_id = newVals.room_id
        if (Object.keys(updateData).length > 0) {
          await supabase.from('training_sessions').update(updateData).eq('id', cr.session_id)
        }
      } else if (cr?.change_type === 'cancel') {
        await supabase.from('training_sessions').update({ status: 'cancelled' }).eq('id', cr.session_id)
      }

      toast.success('Modification acceptee')

      if (cr?.requested_by) {
        const { data: sas } = await supabase.from('profiles').select('id').eq('role', 'super_admin')
        const recipientIds = [...new Set([cr.requested_by, ...(sas || []).map(s => s.id)])]
        supabase.from('in_app_notifications').insert(recipientIds.map(uid => ({
          user_id: uid,
          center_id: user.establishmentId,
          title: 'Modification acceptee',
          message: `${user.firstName} ${user.lastName} a accepte la modification`,
          type: 'change_request_accepted',
          link: '/teacher-collab',
          session_id: cr.session_id,
        }))).then(() => {})
      }

      setChangeRequests(prev => prev.map(c => c.id === id ? { ...c, status: 'accepted', teacherResponse: response } : c))
    } catch (err: any) {
      toast.error(err.message || 'Erreur')
    }
  }, [user])

  const reject = useCallback(async (id: string, response?: string) => {
    if (isDemoMode || !user) return
    try {
      const { data: cr, error: fetchErr } = await supabase.from('session_change_requests')
        .select('session_id, requested_by')
        .eq('id', id)
        .single()
      if (fetchErr) throw fetchErr

      const { error } = await supabase.from('session_change_requests')
        .update({ status: 'rejected', teacher_response: response || null, responded_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error

      toast.success('Modification refusee')

      if (cr?.requested_by) {
        const { data: sas2 } = await supabase.from('profiles').select('id').eq('role', 'super_admin')
        const recipientIds2 = [...new Set([cr.requested_by, ...(sas2 || []).map(s => s.id)])]
        supabase.from('in_app_notifications').insert(recipientIds2.map(uid => ({
          user_id: uid,
          center_id: user.establishmentId,
          title: 'Modification refusee',
          message: `${user.firstName} ${user.lastName} a refuse la modification`,
          type: 'change_request_rejected',
          link: '/teacher-collab',
          session_id: cr.session_id,
        }))).then(() => {})
      }

      setChangeRequests(prev => prev.map(c => c.id === id ? { ...c, status: 'rejected', teacherResponse: response } : c))
    } catch (err: any) {
      toast.error(err.message || 'Erreur')
    }
  }, [user])

  // ==================== TEACHER → ADMIN (reschedule requests) ====================

  /** Check if teacher already has a pending reschedule for this session */
  const hasPendingReschedule = useCallback((sessionId: string): boolean => {
    return changeRequests.some(
      cr => cr.sessionId === sessionId && cr.requestedBy === cr.teacherId && cr.status === 'pending'
    )
  }, [changeRequests])

  /** Teacher creates a reschedule request for one of their sessions */
  const createTeacherRequest = useCallback(async (data: CreateTeacherRescheduleData) => {
    if (isDemoMode || !user) return

    // Validate proposed date is not in the past
    const proposedDateTime = new Date(`${data.proposedDate}T${data.proposedStartTime}:00`)
    if (proposedDateTime < new Date()) {
      toast.error('La date proposée ne peut pas être dans le passé')
      return
    }

    // Validate end time > start time
    if (data.proposedEndTime <= data.proposedStartTime) {
      toast.error('L\'heure de fin doit être après l\'heure de début')
      return
    }

    try {
      // Check for duplicate pending request
      const { data: existing } = await supabase
        .from('session_change_requests')
        .select('id')
        .eq('session_id', data.sessionId)
        .eq('teacher_id', user.id)
        .eq('requested_by', user.id)
        .eq('status', 'pending')
        .limit(1)

      if (existing && existing.length > 0) {
        toast.error('Vous avez déjà une demande de report en attente pour cette séance')
        return
      }

      const proposedStartISO = localToISO(data.proposedDate, data.proposedStartTime)
      const proposedEndISO = localToISO(data.proposedDate, data.proposedEndTime)

      const { error } = await supabase.from('session_change_requests').insert({
        session_id: data.sessionId,
        teacher_id: user.id,
        center_id: user.establishmentId,
        change_type: 'time_change',
        old_values: { start_time: data.currentStart, end_time: data.currentEnd },
        new_values: { start_time: proposedStartISO, end_time: proposedEndISO, reason: data.reason, message: data.message || '' },
        requested_by: user.id,
        message: data.message || `Indisponibilité (${REASON_LABELS[data.reason] || data.reason})`,
      })
      if (error) throw error

      toast.success('Demande de report envoyée')

      // Notify all admins/coordinators of the center + super_admins
      const { data: centerAdmins } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('center_id', user.establishmentId)
        .in('role', ['admin', 'coordinator'])
      const { data: superAdmins } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('role', 'super_admin')
      const seen = new Set<string>()
      const admins = [...(centerAdmins || []), ...(superAdmins || [])].filter(a => {
        if (seen.has(a.id)) return false
        seen.add(a.id)
        return true
      })

      if (admins && admins.length > 0) {
        // In-app notifications
        const notifRows = admins.map(a => ({
          user_id: a.id,
          center_id: user.establishmentId,
          title: 'Demande de report de séance',
          message: `${user.firstName} ${user.lastName} demande le report de "${data.sessionTitle}" (${REASON_LABELS[data.reason] || data.reason})`,
          type: 'change_request_pending',
          link: '/teacher-collab',
          session_id: data.sessionId,
        }))
        supabase.from('in_app_notifications').insert(notifRows).then(({ error: nErr }) => {
          if (nErr) console.error('Notif insert error:', nErr)
        })

        // Email notifications
        const currentDate = new Date(data.currentStart)
        const proposedDate = new Date(proposedStartISO)
        const appUrl = getAppOrigin()

        notifyCollaboration(
          'teacher_reschedule_request',
          admins.map(a => ({ email: a.email, name: a.full_name, userId: a.id })),
          {
            teacher_name: `${user.firstName} ${user.lastName}`,
            session_title: data.sessionTitle,
            session_date: format(currentDate, 'EEEE d MMMM yyyy', { locale: fr }),
            current_time: `${format(currentDate, 'HH:mm')} à ${format(new Date(data.currentEnd), 'HH:mm')}`,
            proposed_date: format(proposedDate, 'EEEE d MMMM yyyy', { locale: fr }),
            proposed_time: `${data.proposedStartTime} à ${data.proposedEndTime}`,
            reason: REASON_LABELS[data.reason] || data.reason,
            message: data.message || '',
            link: `${appUrl}/teacher-collab`,
          },
          user.establishmentId,
        )
      }

      // Refetch to update local state
      await fetchChangeRequests({ teacherId: user.id })
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de l\'envoi de la demande')
      throw err // Re-throw so caller can handle
    }
  }, [user, notifyCollaboration, fetchChangeRequests])

  /** Admin approves a teacher-initiated reschedule request */
  const approveTeacherRequest = useCallback(async (id: string, response?: string) => {
    if (isDemoMode || !user) return

    // Optimistic update
    setChangeRequests(prev => prev.map(c => c.id === id ? { ...c, status: 'accepted', teacherResponse: response } : c))

    try {
      const { data: cr, error: fetchErr } = await supabase.from('session_change_requests')
        .select('session_id, teacher_id, new_values, change_type, session:training_sessions!session_change_requests_session_id_fkey(title), teacher:profiles!session_change_requests_teacher_id_fkey(full_name, email)')
        .eq('id', id)
        .single()
      if (fetchErr) throw fetchErr

      const { error } = await supabase.from('session_change_requests')
        .update({ status: 'accepted', teacher_response: response || null, responded_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error

      // Apply new_values to training_sessions
      if (cr) {
        const newVals = cr.new_values as Record<string, unknown>
        const updateData: Record<string, unknown> = {}
        if (newVals.start_time) updateData.start_time = newVals.start_time
        if (newVals.end_time) updateData.end_time = newVals.end_time
        if (Object.keys(updateData).length > 0) {
          await supabase.from('training_sessions').update(updateData).eq('id', cr.session_id)
        }
      }

      toast.success('Report accepté — séance mise à jour')

      // Notify teacher
      if (cr?.teacher_id) {
        supabase.from('in_app_notifications').insert({
          user_id: cr.teacher_id,
          center_id: user.establishmentId,
          title: 'Report accepté ✓',
          message: `Votre demande de report a été acceptée par ${user.firstName} ${user.lastName}${response ? ` — ${response}` : ''}`,
          type: 'change_request_accepted',
          link: '/teacher-collab',
          session_id: cr.session_id,
        }).then(() => {})

        const teacher = cr.teacher as any
        const session = cr.session as any
        if (teacher?.email) {
          const appUrl = getAppOrigin()
          notifyCollaboration(
            'teacher_reschedule_decision',
            [{ email: teacher.email, name: teacher.full_name, userId: cr.teacher_id }],
            {
              session_title: session?.title || 'Séance',
              decision: 'acceptée',
              admin_name: `${user.firstName} ${user.lastName}`,
              admin_response: response || '',
              link: `${appUrl}/teacher-collab`,
            },
            user.establishmentId,
          )
        }
      }
    } catch (err: any) {
      // Revert optimistic update
      setChangeRequests(prev => prev.map(c => c.id === id ? { ...c, status: 'pending', teacherResponse: undefined } : c))
      toast.error(err.message || 'Erreur')
    }
  }, [user, notifyCollaboration])

  /** Admin rejects a teacher-initiated reschedule request */
  const rejectTeacherRequest = useCallback(async (id: string, response?: string) => {
    if (isDemoMode || !user) return

    // Optimistic update
    setChangeRequests(prev => prev.map(c => c.id === id ? { ...c, status: 'rejected', teacherResponse: response } : c))

    try {
      const { data: cr, error: fetchErr } = await supabase.from('session_change_requests')
        .select('session_id, teacher_id, session:training_sessions!session_change_requests_session_id_fkey(title), teacher:profiles!session_change_requests_teacher_id_fkey(full_name, email)')
        .eq('id', id)
        .single()
      if (fetchErr) throw fetchErr

      const { error } = await supabase.from('session_change_requests')
        .update({ status: 'rejected', teacher_response: response || null, responded_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error

      toast.success('Report refusé')

      if (cr?.teacher_id) {
        supabase.from('in_app_notifications').insert({
          user_id: cr.teacher_id,
          center_id: user.establishmentId,
          title: 'Report refusé',
          message: `Votre demande de report a été refusée par ${user.firstName} ${user.lastName}${response ? ` — ${response}` : ''}`,
          type: 'change_request_rejected',
          link: '/teacher-collab',
          session_id: cr.session_id,
        }).then(() => {})

        const teacher = cr.teacher as any
        const session = cr.session as any
        if (teacher?.email) {
          const appUrl = getAppOrigin()
          notifyCollaboration(
            'teacher_reschedule_decision',
            [{ email: teacher.email, name: teacher.full_name, userId: cr.teacher_id }],
            {
              session_title: session?.title || 'Séance',
              decision: 'refusée',
              admin_name: `${user.firstName} ${user.lastName}`,
              admin_response: response || '',
              link: `${appUrl}/teacher-collab`,
            },
            user.establishmentId,
          )
        }
      }
    } catch (err: any) {
      // Revert optimistic update
      setChangeRequests(prev => prev.map(c => c.id === id ? { ...c, status: 'pending', teacherResponse: undefined } : c))
      toast.error(err.message || 'Erreur')
    }
  }, [user, notifyCollaboration])

  return {
    changeRequests, isLoading,
    fetchChangeRequests, fetchAllForCenter, getForTeacher, getPending, getTeacherInitiated,
    pendingTeacherRescheduleCount, hasPendingReschedule,
    create, accept, reject,
    createTeacherRequest, approveTeacherRequest, rejectTeacherRequest,
  }
}
